import {
  BaseDataGrid,
  ButtonRow,
  DATE_FORMAT_ISO_NO_TIME,
  Dayjs,
  getFilteredColDefs,
  useGridServerPagination,
  useSearchParamsDate,
  useSearchParamsForm,
} from "@synota-io/synota-shared-ui";
import { generatePath, Link, useLocation, useParams } from "react-router-dom";
import { IPayment, usePayments } from "../../../../api/hooks/usePayments";
import { paymentFields } from "../fields/paymentFields";
import {
  DataGridProps,
  GridColumnVisibilityModel,
  GridInputRowSelectionModel,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { TableSummaryToolbar } from "../../../../shared/datagrid/TableSummaryToolbar";
import { TableDateFiltersToolbar } from "../../../../shared/datagrid/TableDateFiltersToolbar";
import { Button } from "@mui/material";
import { CONTRACT_PAYMENTS_PATH } from "../../../../paths";
import { usePaymentsReport } from "../../../../api/hooks/usePaymentsReport";
import { useContractDetails } from "../../../../api/hooks/useContractDetails";

type Props = Pick<DataGridProps<IPayment>, "slots" | "slotProps" | "onRowDoubleClick" | "sx"> & {
  summary?: boolean;
};

export const PaymentsTable = ({ summary, ...props }: Props) => {
  const [rowSelectionModel, onRowSelectionModelChange] = useState<GridInputRowSelectionModel>([]);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});

  const { state } = useLocation();
  const { control, watch } = useSearchParamsForm<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  }>(
    {
      defaultValues: {
        end_date: null,
        start_date: null,
      },
    },
    {
      start_date: useSearchParamsDate(),
      end_date: useSearchParamsDate(),
    },
  );

  const values = watch();

  const params = useParams<{
    id: string;
  }>();

  const [paginationModel, onPaginationModelChange] = useState<GridPaginationModel>(() =>
    summary
      ? { page: 0, pageSize: 5 }
      : {
          page: 0,
          pageSize: 30,
        },
  );

  const input = {
    contract_uuid: params.id || "",
    energy_date_start: values.start_date?.format(DATE_FORMAT_ISO_NO_TIME),
    energy_date_end: values.end_date?.format(DATE_FORMAT_ISO_NO_TIME),
  };

  const {
    payments,
    hasNextPage,
    isLoading: isLoadingPayments,
  } = usePayments({
    ...input,
    page: paginationModel.page,
    number_to_return: paginationModel.pageSize,
  });

  const { contract, isPending: isPendingContract } = useContractDetails({
    id: params.id,
  });

  const { isAvailable, download, isPending: isPendingReport } = usePaymentsReport(input, {
    contract_name:
      contract?.supplier_contract_name ||
      contract?.consumer_contract_name ||
      contract?.utility_service_id ||
      "Contract",
  });

  const paginationProps = useGridServerPagination({
    hasNextPage,
    currentPageRowCount: payments.length,
    paginationModel,
    onPaginationModelChange,
  });

  const columns = getFilteredColDefs(paymentFields, summary ? "summary" : "table", summary).map(
    (c) => ({
      ...c,
      flex: 1,
    }),
  );

  useEffect(() => {
    if (state && "selectedRow" in state && state.selectedRow) {
      onRowSelectionModelChange([state.selectedRow]);
    }
  }, [state]);

  const isLoading = isLoadingPayments || isPendingReport || isPendingContract;

  return (
    <BaseDataGrid
      {...props}
      sx={summary ? { ...props.sx, minHeight: "280px" } : props.sx}
      slots={{
        footer: summary
          ? () => (
              <ButtonRow borderTop="solid" borderColor="background.default">
                <Button
                  component={Link}
                  to={generatePath(CONTRACT_PAYMENTS_PATH, { id: params.id! })}
                  size="small"
                  fullWidth
                >
                  See all
                </Button>
              </ButtonRow>
            )
          : undefined,
        toolbar: summary
          ? TableSummaryToolbar
          : (props) => (
              <TableDateFiltersToolbar
                control={control}
                isLoading={isLoading}
                onDownloadClick={isAvailable ? download : undefined}
                {...props}
              />
            ),
        ...props.slots,
      }}
      density={summary ? "compact" : "standard"}
      autosizeOnMount={!summary}
      getRowId={(row) => row.shared_uuid}
      columns={columns}
      columnVisibilityModel={columnVisibility}
      onColumnVisibilityModelChange={setColumnVisibility}
      onRowSelectionModelChange={onRowSelectionModelChange}
      rowSelectionModel={rowSelectionModel}
      {...paginationProps}
      loading={isLoading}
      rows={payments}
    />
  );
};
